import { SolType } from '../models/core/sol-type';
import { SolViewId } from '../models/core/sol-view-id';
import { SolTableDef } from '../models/overview/sol-table-def';
import { SolTableLayout, SolVisibleColumn } from '../models/overview/sol-table';
import { SolTableSort } from '../models/overview/sol-table-sort';

export class TableTools {
  /** The narrowest and widest a resized column may be, both included; the backend stores the same range. */
  public static readonly MIN_COLUMN_WIDTH = 64;
  public static readonly MAX_COLUMN_WIDTH = 1440;

  private static readonly natives: Map<SolType<any>, SolViewId<any>> = new Map<SolType<any>, SolViewId<any>>([
    [SolType.string, SolViewId.textField],
    [SolType.text, SolViewId.text],
    [SolType.number, SolViewId.number],
    [SolType.date, SolViewId.date],
    [SolType.icon, SolViewId.icon],
  ]);

  public static isPrimitive(type: SolType<any>, viewId?: SolViewId<any>): boolean {
    return TableTools.natives.has(type) && (!viewId || TableTools.natives.get(type) === viewId);
  }

  public static toPrimitiveDefaultOptions(type: SolType<any>, viewId?: SolViewId<any>): any {
    if (viewId !== undefined) {
      return viewId.defaultOptions;
    }
    return TableTools.natives.get(type)?.toOptions(type) || {};
  }

  public static hasTextOptions(isPrimitive: boolean, type: SolType<any>): boolean {
    return (
      isPrimitive &&
      (type.isA(SolType.string) || type.isA(SolType.text) || type.isA(SolType.number) || type.isA(SolType.date))
    );
  }

  public static toOption(viewId: SolViewId<any> | undefined, options: any | undefined, field: string): any {
    if (options && options.hasOwnProperty(field)) {
      return options[field];
    }
    return viewId?.defaultOptions[field];
  }

  public static isColumnWidth(value: unknown): value is number {
    return (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= TableTools.MIN_COLUMN_WIDTH &&
      value <= TableTools.MAX_COLUMN_WIDTH
    );
  }

  public static toColumnWidth(width: number): number {
    const rounded = Number.isNaN(width) ? TableTools.MIN_COLUMN_WIDTH : Math.round(width);
    return Math.min(TableTools.MAX_COLUMN_WIDTH, Math.max(TableTools.MIN_COLUMN_WIDTH, rounded));
  }

  public static toVisibleColumns(definition: SolTableDef<any>): SolVisibleColumn[] {
    return definition.columns.map(column => ({
      id: column.id,
      visible: column.visible ?? true,
    }));
  }

  /** The layout a definition describes on its own, before any stored one is applied. */
  public static toLayout(definition: SolTableDef<any>): SolTableLayout {
    return { columns: TableTools.toVisibleColumns(definition), sort: definition.defaultSort };
  }

  /**
   * Narrows a stored preference value to a layout; a wrong shape reads as absent, not an error. A width
   * outside the supported range leaves that column automatic rather than dropping the whole layout.
   */
  public static toStoredLayout(value: unknown): SolTableLayout | undefined {
    const columns = TableTools.toStoredColumns((value as { columns?: unknown } | null)?.columns);
    if (columns === undefined) {
      return undefined;
    }
    return { columns, sort: TableTools.toStoredSort((value as { sort?: unknown }).sort) };
  }

  private static toStoredColumns(value: unknown): SolVisibleColumn[] | undefined {
    if (!Array.isArray(value)) {
      return undefined;
    }
    const columns: SolVisibleColumn[] = [];
    for (const entry of value) {
      const id = entry?.['id'];
      const visible = entry?.['visible'];
      const width = entry?.['width'];
      if (typeof id !== 'string' || typeof visible !== 'boolean') {
        return undefined;
      }
      if (width !== undefined && typeof width !== 'number') {
        return undefined;
      }
      columns.push(TableTools.isColumnWidth(width) ? { id, visible, width } : { id, visible });
    }
    return columns;
  }

  /** A stored sort naming no column, or a direction the table cannot apply, is no sort at all. */
  private static toStoredSort(value: unknown): SolTableSort | undefined {
    const columnId = (value as { columnId?: unknown } | null)?.columnId;
    const direction = (value as { direction?: unknown } | null)?.direction;
    if (typeof columnId !== 'string' || (direction !== 'asc' && direction !== 'desc')) {
      return undefined;
    }
    return { columnId, direction };
  }
}
